import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConfirmation } from '../ui/ConfirmationDialog';
import { Users, Briefcase, Eye, UserPlus, Search, ArrowLeft, Upload, Download, ExternalLink, ChevronDown, User, MoreVertical, FileText, Edit, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { BulkUploadResumesModal } from '../candidates/BulkUploadResumesModal';
import { AssignmentModal } from './AssignmentModal';
import { BulkAssignmentModal } from './BulkAssignmentModal';
import { StatusUpdateModal } from '../candidates/StatusUpdateModal';
import FilterDrawer from '../ui/FilterDrawer';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { useRequirementCandidates, useRequirement } from '../../lib/hooks/useApi';
import { useToast } from '../ui/Toast';
import EdgeActions from '../ui/EdgeActions';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../lib/api/client';
import { PortalDropdown } from '../ui/PortalDropdown';
import { LoadingSpinner } from '../ui/LoadingSpinner';

const RequirementCandidatesPage: React.FC = () => {
  const { requirementId } = useParams<{ requirementId: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const actionButtonRef = useRef<HTMLElement | null>(null);
  const buttonRefsMap = useRef<Map<string, HTMLElement>>(new Map());
  const buttonRefObjectsMap = useRef<Map<string, React.RefObject<HTMLElement>>>(new Map());
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('fitment_score');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortKey, setSortKey] = useState('fitment_score_desc');

  const [statusFilter, setStatusFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { toast } = useToast();
  const { hasPermission, hasRole, user } = useAuth();
  const isAdmin = Boolean(
    hasRole('admin') ||
    hasPermission('all:admin') ||
    hasPermission('admin:access') ||
    user?.scopes?.includes('system:manage')
  );
  
  // Assignment states
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [bulkAssignmentModalOpen, setBulkAssignmentModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [selectedCandidatesForBulk, setSelectedCandidatesForBulk] = useState<any[]>([]);
  const [recruiters, setRecruiters] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Status update states
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [selectedCandidateForStatusUpdate, setSelectedCandidateForStatusUpdate] = useState<any>(null);
  const [reprocessingCandidateId, setReprocessingCandidateId] = useState<string | null>(null);

  // Fetch requirement details
  const { data: requirementData } = useRequirement(requirementId || '');

  // Fetch candidates with a reasonable page size
  // Note: Backend has a max page_size limit of 500
  const { data, isLoading, error, refetch } = useRequirementCandidates(
    requirementId || '', 
    1, // Always fetch page 1
    500 // Maximum allowed page size to get most candidates
  );


  React.useEffect(() => {
    const handler = () => setBulkOpen(true);
    window.addEventListener('open-bulk-upload', handler);
    return () => window.removeEventListener('open-bulk-upload', handler);
  }, []);

  // Fetch recruiters for assignment (HR Coordinators only)
  useEffect(() => {
    const fetchRecruiters = async () => {
      if (!hasPermission('candidates:assign') || !requirementId) return;
      
      try {
        const recruitersList = await apiClient.getRecruitersForRequirement(requirementId);
        setRecruiters(recruitersList);
      } catch (error) {
        console.error('Failed to fetch recruiters:', error);
        // Fallback to empty list if API fails
        setRecruiters([]);
      }
    };
    
    fetchRecruiters();
  }, [hasPermission, requirementId]);

  const allCandidates = data?.candidates || [];
  
  // Client-side filtering, sorting, and pagination
  const filteredAndSortedCandidates = useMemo(() => {
    let filtered = allCandidates.filter((candidate: any) => {
      // Fuzzy search across name, email, and skills
      const matchesSearch = !searchTerm || 
        candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.parsed_fields?.skills?.some((skill: string) => 
          skill.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesStatus = statusFilter === 'all' || 
        (candidate.submission_status || 'submitted') === statusFilter;

      const score = candidate.fitment_score || 0;
      const matchesScore = scoreFilter === 'all' ||
        (scoreFilter === 'excellent' && score >= 80) ||
        (scoreFilter === 'good' && score >= 60 && score < 80) ||
        (scoreFilter === 'needs_review' && score < 60);

      // Assignment status filter
      const openToAll = candidate.open_to_all;
      const assignedToUserId = candidate.assigned_to_user_id;
      let matchesAssigned = true;
      if (assignedFilter === 'assigned') {
        matchesAssigned = !!(assignedToUserId && !openToAll);
      } else if (assignedFilter === 'unassigned') {
        matchesAssigned = !assignedToUserId && !openToAll;
      } else if (assignedFilter === 'open_to_all') {
        matchesAssigned = !!openToAll;
      }

      return matchesSearch && matchesStatus && matchesScore && matchesAssigned;
    });

    // Sort candidates
    filtered.sort((a: any, b: any) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'fitment_score') {
        aValue = a.fitment_score || 0;
        bValue = b.fitment_score || 0;
      } else if (sortField === 'created_at') {
        aValue = new Date(a.submission_created_at || 0).getTime();
        bValue = new Date(b.submission_created_at || 0).getTime();
      } else if (sortField === 'name') {
        aValue = a.name || '';
        bValue = b.name || '';
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });


    return filtered;
  }, [allCandidates, searchTerm, statusFilter, scoreFilter, assignedFilter, sortField, sortDirection, sortKey]);

  // Client-side pagination
  const totalFiltered = filteredAndSortedCandidates.length;
  const totalPages = Math.ceil(totalFiltered / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCandidates = filteredAndSortedCandidates.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, scoreFilter, assignedFilter, sortField, sortDirection]);

  const handleViewAnalysis = (candidateId: string) => {
    navigate(`/candidates/${candidateId}/analysis/${requirementId}`);
  };


  const handleDownloadResume = async (candidateId: string) => {
    try {
      const { url } = await (await import('../../lib/api/client')).apiClient.getCandidateResumeUrl(candidateId, { disposition: 'attachment', requirementId: requirementId || undefined });
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Failed to get resume URL', e);
      confirm({
        title: 'Download Failed',
        description: 'Failed to download resume. Please try again.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
    }
  };

  const handleOpenResume = async (candidateId: string) => {
    try {
      const { url } = await (await import('../../lib/api/client')).apiClient.getCandidateResumeUrl(candidateId, { disposition: 'inline', requirementId: requirementId || undefined });
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      console.error('Failed to open resume', e);
      confirm({
        title: 'Open Failed',
        description: 'Failed to open resume. Please try again.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
    }
  };

  const resolveRequirementIdForCandidate = (candidate: any): string | undefined => {
    if (!candidate) return requirementId || undefined;
    return (
      candidate.requirement_id ||
      candidate.requirementId ||
      candidate.submission_requirement_id ||
      candidate.submission?.requirement_id ||
      candidate.submissions?.[0]?.requirement_id ||
      candidate.analysis_results?.requirement_id ||
      requirementId ||
      undefined
    );
  };

  const handleAdminReprocessCandidate = (candidate: any) => {
    const candidateId = candidate?.candidate_id || candidate?.id;
    const targetRequirementId = resolveRequirementIdForCandidate(candidate);

    if (!candidateId || !targetRequirementId) {
      toast({ title: 'Missing data', description: 'Candidate or requirement ID is missing for reprocess.', variant: 'error', duration: 3000 });
      return;
    }

    setOpenMenuId(null);

    confirm({
      title: 'Re-run JD vs CV analysis?',
      description: 'This will rerun the AI analysis for this candidate using the existing resume.',
      variant: 'warning',
      confirmText: 'Reprocess',
      onConfirm: async () => {
        try {
          setReprocessingCandidateId(candidateId);
          const response = await apiClient.adminReprocessCandidateJDCV(candidateId, targetRequirementId, { clearIdentity: true });
          toast({ title: 'Reprocessing queued', description: response?.message || 'JD vs CV analysis will be rerun shortly.', variant: 'success', duration: 3000 });
          await refetch();
        } catch (error: any) {
          const detail = error?.response?.data?.detail || error?.message || 'Failed to queue reprocessing';
          toast({ title: 'Reprocess failed', description: detail, variant: 'error', duration: 4000 });
          throw error;
        } finally {
          setReprocessingCandidateId(null);
        }
      },
    });
  };

  // Assignment handlers
  const handleAssignCandidate = (candidate: any) => {
    setSelectedCandidate(candidate);
    setAssignmentModalOpen(true);
  };

  const handleAssignmentSubmit = async (recruiterId: string, openToAll: boolean, reason?: string) => {
    if (!selectedCandidate || !requirementId) return;
    
    setIsAssigning(true);
    try {
      if (openToAll) {
        // Set as open to all - using apiClient to ensure correct base URL
        await apiClient.client.put(`/api/v1/requirements/${requirementId}/candidates/${selectedCandidate.candidate_id}/open-to-all`, {
          candidate_id: selectedCandidate.candidate_id,
          reason: reason
        });
        toast({ title: 'Success', description: 'Candidate set as open to all', variant: 'success', duration: 3000 });
      } else {
        // Assign to specific recruiter
        await apiClient.client.post(`/api/v1/requirements/${requirementId}/candidates/assign`, {
          candidate_id: selectedCandidate.candidate_id,
          recruiter_id: recruiterId,
          reason: reason
        });
        toast({ title: 'Success', description: 'Candidate assigned successfully', variant: 'success', duration: 3000 });
      }
      setAssignmentModalOpen(false);
      setSelectedCandidate(null);
      refetch();
    } catch (error: any) {
      console.error('Assignment failed:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to assign candidate', variant: 'error', duration: 3000 });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleBulkAssignmentSubmit = async (
    candidateIds: string[],
    strategy: 'single' | 'round_robin' | 'open_to_all',
    recruiterId?: string,
    recruiterList?: string[],
    reason?: string
  ) => {
    if (!requirementId) return;
    
    setIsAssigning(true);
    try {
      // Using apiClient to ensure correct base URL
      const payload: any = {
        candidate_ids: candidateIds,
        assignment_strategy: strategy,
        reason: reason
      };
      
      if (strategy === 'single' && recruiterId) {
        payload.recruiter_id = recruiterId;
      } else if (strategy === 'round_robin' && recruiterList && recruiterList.length > 0) {
        payload.recruiter_list = recruiterList;
      }
      
      await apiClient.client.post(`/api/v1/requirements/${requirementId}/candidates/bulk-assign`, payload);
      toast({ title: 'Success', description: `Successfully assigned ${candidateIds.length} candidate(s)`, variant: 'success', duration: 3000 });
      setBulkAssignmentModalOpen(false);
      setSelectedCandidatesForBulk([]);
      refetch();
    } catch (error: any) {
      console.error('Bulk assignment failed:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to assign candidates', variant: 'error', duration: 3000 });
    } finally {
      setIsAssigning(false);
    }
  };

  // Check if user can assign candidates
  const canAssign = hasPermission('candidates:assign');

  const getScoreColor = (score: number) => {
    const s = Math.max(0, Math.min(100, Math.round(score)));
    const bucket = Math.floor(s / 5);
    const palette = [
      'text-red-700 bg-red-100/60', 'text-red-700 bg-red-100/60', 'text-red-600 bg-red-100/50', 'text-red-500 bg-red-100/40',
      'text-orange-600 bg-orange-100/50', 'text-orange-500 bg-orange-100/50', 'text-amber-600 bg-amber-100/50', 'text-amber-500 bg-amber-100/50',
      'text-yellow-600 bg-yellow-100/50', 'text-yellow-500 bg-yellow-100/50', 'text-lime-600 bg-lime-100/50', 'text-lime-500 bg-lime-100/50',
      'text-green-600 bg-green-100/50', 'text-green-500 bg-green-100/50', 'text-green-500 bg-green-100/50', 'text-emerald-600 bg-emerald-100/50',
      'text-emerald-500 bg-emerald-100/50', 'text-teal-600 bg-teal-100/50', 'text-teal-500 bg-teal-100/50', 'text-teal-500 bg-teal-100/50', 'text-teal-500 bg-teal-100/50'
    ];
    return palette[Math.min(palette.length - 1, bucket)];
  };

  const getScoreBarBg = (score: number) => {
    const s = Math.max(0, Math.min(100, Math.round(score)));
    const bucket = Math.floor(s / 5);
    const barPalette = [
      'bg-red-600','bg-red-600','bg-red-500','bg-red-500',
      'bg-orange-600','bg-orange-500','bg-amber-600','bg-amber-500',
      'bg-yellow-600','bg-yellow-500','bg-lime-600','bg-lime-500',
      'bg-green-600','bg-green-500','bg-green-500','bg-emerald-600',
      'bg-emerald-500','bg-teal-600','bg-teal-500','bg-teal-500','bg-teal-500'
    ];
    return barPalette[Math.min(barPalette.length - 1, bucket)];
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'submitted': return 'text-blue-600 bg-blue-100/50';
      case 'reviewing': return 'text-yellow-600 bg-yellow-100/50';
      case 'shortlisted': return 'text-green-600 bg-green-100/50';
      case 'rejected': return 'text-red-600 bg-red-100/50';
      case 'hired': return 'text-purple-600 bg-purple-100/50';
      default: return 'text-gray-600 bg-gray-100/50';
    }
  };

  const getAssignmentRowBgColor = (candidate: any) => {
    const openToAll = candidate.open_to_all;
    const assignedToUserId = candidate.assigned_to_user_id;
    
    if (openToAll) {
      return 'bg-glass-warning/5 border-l-2 border-warning-500/30'; // Light amber glass for Open to All
    } else if (assignedToUserId) {
      return 'bg-green-50/20 dark:bg-green-900/10 border-l-2 border-green-400/20'; // Green tint for Assigned
    }
    return 'bg-glass-surface/40 border-l-2 border-glass-border/20'; // Glass surface for Unassigned
  };

  const getAssignmentBadge = (candidate: any) => {
    const openToAll = candidate.open_to_all;
    const assignedToUserId = candidate.assigned_to_user_id;
    const assignedToName = candidate.assigned_to_name;
    
    if (openToAll) {
      return {
        text: 'Open to All',
        color: 'bg-glass-warning/40 border border-warning-500/30 text-warning-700 dark:text-warning-400',
        icon: '🌟'
      };
    } else if (assignedToUserId && assignedToName) {
      // Show recruiter name when assigned
      return {
        text: assignedToName,
        color: 'bg-glass-success/40 border border-success-500/30 text-success-700 dark:text-success-400',
        icon: '🔵'
      };
    } else if (assignedToUserId) {
      // Assigned but no name available
      return {
        text: 'Assigned',
        color: 'bg-glass-success/40 border border-success-500/30 text-success-700 dark:text-success-400',
        icon: '🔵'
      };
    } else {
      // Unassigned
      return {
        text: 'Unassigned',
        color: 'bg-glass-surface/60 text-text-enterprise-tertiary border-glass-border/40',
        icon: '⚪'
      };
    }
  };

  if (isLoading) {
    return (
      <ResponsiveContainer>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="xl" variant="ai" />
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (error) {
    return (
      <ResponsiveContainer>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Error Loading Candidates
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {error instanceof Error ? error.message : 'An error occurred while loading candidates.'}
              </p>
              <Button onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12">
          {/* Back to Requirements List button */}
          <button
            onClick={() => navigate('/requirements')}
            className="mb-4 inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            aria-label="Back to Requirements List"
            title="Back to Requirements List"
          >
            <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Back to Requirements</span>
            <span className="sm:hidden">Back</span>
          </button>

          {/* Header with Search and Sort */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                  {requirementData ? (() => {
                    const requirementName = requirementData.title || 'Untitled Requirement';
                    const clientName = requirementData.client?.name || requirementData.client_name;
                    
                    // If requirement name is too long (>50 chars), show shortened version with client
                    if (requirementName.length > 50) {
                      const shortName = requirementName.substring(0, 47) + '...';
                      return clientName ? `${shortName} (${clientName})` : shortName;
                    }
                    
                    // If requirement name is reasonable length, show it with client in parentheses
                    return clientName ? `${requirementName} (${clientName})` : requirementName;
                  })() : 'Requirement Candidates'}
                </h1>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                {/* Search Input */}
                <div className="relative flex-1 sm:flex-initial sm:min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* Sort Dropdown */}
                <div className="relative flex-1 sm:flex-initial sm:min-w-[180px]">
                  <select
                    value={`${sortField}_${sortDirection}`}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Split on the last underscore to handle field names with underscores
                      const lastUnderscoreIndex = value.lastIndexOf('_');
                      const field = value.substring(0, lastUnderscoreIndex);
                      const direction = value.substring(lastUnderscoreIndex + 1);
                      setSortField(field);
                      setSortDirection(direction as 'asc' | 'desc');
                      setSortKey(value);
                    }}
                    className="w-full appearance-none pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="fitment_score_desc">Fitment Score (High to Low)</option>
                    <option value="fitment_score_asc">Fitment Score (Low to High)</option>
                    <option value="created_at_desc">Newest First</option>
                    <option value="created_at_asc">Oldest First</option>
                    <option value="name_asc">Name (A-Z)</option>
                    <option value="name_desc">Name (Z-A)</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>

                {/* Assigned Status Filter */}
                <div className="relative flex-1 sm:flex-initial sm:min-w-[160px]">
                  <select
                    value={assignedFilter}
                    onChange={(e) => setAssignedFilter(e.target.value)}
                    className="w-full appearance-none pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Assignment</option>
                    <option value="assigned">Assigned</option>
                    <option value="unassigned">Unassigned</option>
                    <option value="open_to_all">Open to All</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>
            
            {/* Results Summary */}
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Showing {startIndex + 1}-{Math.min(endIndex, totalFiltered)} of {totalFiltered} candidates
              {searchTerm && ` matching "${searchTerm}"`}
              {allCandidates.length >= 500 && (
                <span className="text-amber-600 dark:text-amber-400 ml-2">
                  (Note: Displaying first 500 candidates due to API limits)
                </span>
              )}
            </div>
          </div>

          {/* Candidates List */}
          {paginatedCandidates.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {allCandidates.length === 0 ? 'No Candidates Yet' : 'No Matching Candidates'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                {allCandidates.length === 0 
                  ? 'No candidates have been submitted to this requirement yet. Start by adding the first candidate.'
                  : 'Try adjusting your search criteria or filters to find candidates.'
                }
              </p>
              <Button 
                onClick={() => navigate(`/candidates/submit/${requirementId}`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Candidate
              </Button>
              <div className="mt-3" />
              <Button 
                onClick={() => setBulkOpen(true)}
                variant="outline"
                className="px-6 py-2 rounded-lg border-gray-300 dark:border-gray-600"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload Resumes
              </Button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-x-auto">
              {/* Mobile Card View */}
              <div className="block lg:hidden p-3 sm:p-4 space-y-3 sm:space-y-4">
                {paginatedCandidates.map((candidate: any, index: number) => {
                  const fitmentScore = candidate.fitment_score || 
                                    candidate.analysis_results?.analysis?.overall_fitment_score || 
                                    0;
                  const assignmentBadge = getAssignmentBadge(candidate);
                  const rowBgColor = getAssignmentRowBgColor(candidate);
                  
                  return (
                    <div key={candidate.candidate_id || candidate.id} className={`rounded-lg p-4 border border-gray-200 dark:border-gray-600 ${rowBgColor}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                            {index + 1}
                          </div>
                          <div className="min-w-0 flex-1 max-w-[200px] sm:max-w-[250px]">
                            <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white truncate" title={candidate.name || 'Unknown Candidate'}>
                              {candidate.name || 'Unknown Candidate'}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate" title={candidate.parsed_fields?.current_position || 'Position not specified'}>
                              {candidate.parsed_fields?.current_position || 'Position not specified'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold ${getScoreColor(fitmentScore)}`}>
                            {fitmentScore}%
                          </div>
                        </div>
                      </div>
                      
                      {/* Assignment Info */}
                      <div className="mb-3 flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Assigned:</span>
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${assignmentBadge.color}`}>
                          <span>{assignmentBadge.icon}</span>
                          <span>{assignmentBadge.text}</span>
                        </div>
                      </div>
                      
                      {/* Mobile Actions */}
                      <div className="flex items-center justify-end gap-1.5 sm:gap-2">
                        <Button
                          onClick={() => handleViewAnalysis(candidate.candidate_id || candidate.id)}
                          size="sm"
                          className="p-1.5 sm:p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shrink-0"
                          title="View Analysis"
                        >
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            onClick={() => handleAdminReprocessCandidate(candidate)}
                            variant="outline"
                            size="sm"
                            className="p-1.5 sm:p-2 border-purple-200 hover:bg-purple-50 dark:border-purple-700 dark:hover:bg-purple-900/40 rounded-lg shrink-0 text-purple-700 dark:text-purple-300 flex items-center gap-1.5"
                            disabled={reprocessingCandidateId === (candidate.candidate_id || candidate.id)}
                            title="Reprocess JD vs CV analysis"
                          >
                            {reprocessingCandidateId === (candidate.candidate_id || candidate.id) ? (
                              <>
                                <LoadingSpinner size="sm" variant="ai" />
                                <span className="hidden sm:inline">Reprocessing...</span>
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Reprocess</span>
                              </>
                            )}
                          </Button>
                        )}
                        {candidate.cv_path && (
                          <Button 
                            onClick={() => handleDownloadResume(candidate.candidate_id || candidate.id)} 
                            variant="outline" 
                            size="sm" 
                            className="p-1.5 sm:p-2 border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 rounded-lg shrink-0" 
                            title="Download Resume"
                          >
                            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        )}
                        {candidate.cv_path && (
                          <Button 
                            onClick={() => handleOpenResume(candidate.candidate_id || candidate.id)} 
                            variant="ghost" 
                            size="sm" 
                            className="p-1.5 sm:p-2 rounded-lg shrink-0" 
                            title="Open Resume"
                          >
                            <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block relative overflow-visible">
                {/* Bulk Actions Bar (HR Coordinators only) */}
                {canAssign && selectedCandidatesForBulk.length > 0 && (
                  <div className="px-4 sm:px-6 py-3 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-b border-glass-border/50 dark:border-glass-border-dark-mode/50 backdrop-blur-xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                      <span className="text-sm font-medium text-text-enterprise-primary">
                        {selectedCandidatesForBulk.length} candidate(s) selected
                      </span>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                          onClick={() => setSelectedCandidatesForBulk([])}
                          size="sm"
                          variant="outline"
                          className="text-sm flex-1 sm:flex-initial border-glass-border/50 dark:border-glass-border-dark-mode/50 text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70"
                        >
                          Clear Selection
                        </Button>
                        <Button
                          onClick={() => setBulkAssignmentModalOpen(true)}
                          size="sm"
                          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm flex-1 sm:flex-initial shadow-lg shadow-green-500/25"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Bulk Assign
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="overflow-x-auto overflow-y-visible -mx-4 sm:mx-0" style={{ maxWidth: '100%' }}>
                  <div className="w-full align-middle">
                    <table className="w-full divide-y divide-gray-200 dark:divide-gray-700" style={{ tableLayout: 'auto', width: '100%' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      {canAssign && (
                        <th className="px-2 sm:px-3 lg:px-4 py-3 text-left sticky left-0 bg-gray-50 dark:bg-gray-700 z-10 border-r border-gray-200 dark:border-gray-600">
                          <input
                            type="checkbox"
                            checked={paginatedCandidates.length > 0 && paginatedCandidates.every((c: any) => selectedCandidatesForBulk.some(sc => (sc.candidate_id || sc.id) === (c.candidate_id || c.id)))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newCandidates = paginatedCandidates.filter((c: any) => !selectedCandidatesForBulk.some(sc => (sc.candidate_id || sc.id) === (c.candidate_id || c.id)));
                                setSelectedCandidatesForBulk([...selectedCandidatesForBulk, ...newCandidates]);
                              } else {
                                const currentPageIds = paginatedCandidates.map((c: any) => c.candidate_id || c.id);
                                setSelectedCandidatesForBulk(selectedCandidatesForBulk.filter(sc => !currentPageIds.includes(sc.candidate_id || sc.id)));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </th>
                      )}
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                          <span className="hidden sm:inline">Candidate</span>
                          <span className="sm:hidden">Name</span>
                        </div>
                      </th>
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                        Score
                      </th>
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                          <span>Exp.</span>
                        </div>
                      </th>
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                          <span>Assigned</span>
                        </div>
                      </th>
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                        Submission
                      </th>
                      <th className="px-2 sm:px-3 lg:px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-700 z-10 border-l border-gray-200 dark:border-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedCandidates.map((candidate: any) => {
                      const fitmentScore = candidate.fitment_score || candidate.analysis_results?.analysis?.overall_fitment_score || 0;
                      const submissionStatus = candidate.submission_status || 'submitted';
                      const isSelected = selectedCandidatesForBulk.some(sc => (sc.candidate_id || sc.id) === (candidate.candidate_id || candidate.id));
                      
                      const assignmentBadge = getAssignmentBadge(candidate);
                      const rowBgColor = getAssignmentRowBgColor(candidate);
                      
                      return (
                        <tr key={candidate.candidate_id || candidate.id} className={`hover:opacity-80 transition-all ${rowBgColor}`}>
                          {canAssign && (
                            <td className={`px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap sticky left-0 z-10 border-r border-gray-200 dark:border-gray-600 ${rowBgColor}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCandidatesForBulk([...selectedCandidatesForBulk, candidate]);
                                  } else {
                                    setSelectedCandidatesForBulk(selectedCandidatesForBulk.filter(sc => (sc.candidate_id || sc.id) !== (candidate.candidate_id || candidate.id)));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                              />
                            </td>
                          )}
                          <td className="px-2 sm:px-3 lg:px-4 py-3 sm:py-4 min-w-[120px] sm:min-w-[150px]">
                            <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm lg:text-base shrink-0">
                                {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-bold text-gray-900 dark:text-white text-xs sm:text-sm truncate" title={candidate.name || 'Unknown'}>
                                  {candidate.name || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 truncate hidden sm:block" title={candidate.email || 'No email'}>
                                  {candidate.email || 'No email'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap min-w-[90px] hidden md:table-cell">
                            <div className="text-center">
                              <div className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs sm:text-sm font-bold ${getScoreColor(fitmentScore)} whitespace-nowrap mb-1`}>
                                {fitmentScore}%
                              </div>
                              <div className="h-1 sm:h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden w-full max-w-[60px] mx-auto">
                                <div className={`h-full rounded-full ${getScoreBarBg(fitmentScore)}`} style={{ width: `${Math.max(0, Math.min(100, fitmentScore))}%` }}></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap min-w-[80px] hidden lg:table-cell">
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 shrink-0" />
                              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                {((Number(candidate.parsed_fields?.experience_years ?? candidate.experience_years ?? 0) || 0).toFixed(1))}y
                              </span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap min-w-[100px] hidden sm:table-cell">
                            <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md text-xs font-medium ${assignmentBadge.color} whitespace-nowrap`}>
                              <span className="text-xs sm:text-sm">{assignmentBadge.icon}</span>
                              <span className="truncate max-w-[80px]">{assignmentBadge.text}</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap min-w-[100px]">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCandidateForStatusUpdate(candidate);
                                setStatusUpdateModalOpen(true);
                              }}
                              className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs font-medium ${getStatusColor(submissionStatus)} whitespace-nowrap hover:opacity-80 cursor-pointer transition-opacity truncate max-w-[100px]`}
                              title="Click to update status"
                            >
                              {submissionStatus.charAt(0).toUpperCase() + submissionStatus.slice(1)}
                            </button>
                          </td>
                          <td className="px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-600 dark:text-gray-400 min-w-[120px] hidden xl:table-cell">
                            <div className="leading-tight">
                              <div className="font-medium text-gray-900 dark:text-white truncate">
                                {candidate.submitted_by_name || candidate.submitted_by || '—'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-500">
                                {candidate.submission_created_at ? new Date(candidate.submission_created_at).toLocaleDateString() : '—'}
                              </div>
                            </div>
                          </td>
                          <td className={`px-2 sm:px-3 lg:px-4 py-3 sm:py-4 whitespace-nowrap text-right min-w-[50px] sticky right-0 z-10 border-l border-gray-200 dark:border-gray-600 ${rowBgColor}`}>
                            <div className="flex items-center justify-end">
                              <div className="relative">
                                {(() => {
                                  const candidateId = candidate.candidate_id || candidate.id || '';
                                  const isMenuOpen = openMenuId === candidateId;
                                  
                                  // Get or create a stable ref object for this candidate
                                  if (!buttonRefObjectsMap.current.has(candidateId)) {
                                    const refObj = React.createRef<HTMLElement>();
                                    buttonRefObjectsMap.current.set(candidateId, refObj);
                                  }
                                  const buttonRef = buttonRefObjectsMap.current.get(candidateId)!;
                                  
                                  return (
                                    <>
                                      <Button
                                        ref={(el) => {
                                          if (el) {
                                            buttonRefsMap.current.set(candidateId, el);
                                            // @ts-ignore - Assigning to ref object
                                            buttonRef.current = el;
                                          }
                                        }}
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const button = e.currentTarget;
                                          buttonRefsMap.current.set(candidateId, button);
                                          // @ts-ignore - Assigning to ref object
                                          buttonRef.current = button;
                                          actionButtonRef.current = button;
                                          setOpenMenuId(isMenuOpen ? null : candidateId);
                                        }}
                                        className="p-1 sm:p-1.5 lg:p-2 rounded-lg shrink-0"
                                        title="Actions"
                                      >
                                        <MoreVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                      </Button>
                                      
                                      {isMenuOpen && (() => {
                                        // Ensure ref is set from buttonRefsMap if available
                                        const buttonElement = buttonRefsMap.current.get(candidateId);
                                        if (buttonElement && !buttonRef.current) {
                                          // @ts-ignore - Assigning to ref object
                                          buttonRef.current = buttonElement;
                                        }
                                        return buttonRef.current || buttonElement;
                                      })() && (
                                        <PortalDropdown
                                          isOpen={true}
                                          triggerRef={buttonRef}
                                          className="w-48 min-w-[12rem]"
                                          onClose={() => setOpenMenuId(null)}
                                        >
                                  <div className="py-1">
                                    {canAssign && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAssignCandidate(candidate);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 flex items-center gap-2 transition-colors"
                                      >
                                        <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                        Assign
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedCandidateForStatusUpdate(candidate);
                                        setStatusUpdateModalOpen(true);
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 flex items-center gap-2 transition-colors"
                                    >
                                      <Edit className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                      Update Status
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewAnalysis(candidate.candidate_id || candidate.id);
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 flex items-center gap-2 transition-colors"
                                    >
                                      <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                      View Analysis
                                    </button>
                                    {isAdmin && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAdminReprocessCandidate(candidate);
                                        }}
                                        disabled={reprocessingCandidateId === (candidate.candidate_id || candidate.id)}
                                        className="w-full text-left px-4 py-2 text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {reprocessingCandidateId === (candidate.candidate_id || candidate.id) ? (
                                          <LoadingSpinner size="sm" variant="ai" />
                                        ) : (
                                          <RefreshCw className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                        )}
                                        Reprocess JD/CV
                                      </button>
                                    )}
                                    {candidate.cv_path && (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadResume(candidate.candidate_id || candidate.id);
                                            setOpenMenuId(null);
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 flex items-center gap-2 transition-colors"
                                        >
                                          <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                          Download Resume
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenResume(candidate.candidate_id || candidate.id);
                                            setOpenMenuId(null);
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 flex items-center gap-2 transition-colors"
                                        >
                                          <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                          Open Resume
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </PortalDropdown>
                                      )}
                                      </>
                                    );
                                  })()}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
                  Showing <span className="font-semibold">{startIndex + 1}</span> to <span className="font-semibold">{Math.min(endIndex, totalFiltered)}</span> of <span className="font-semibold">{totalFiltered}</span> candidates
                </div>
                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50 flex-1 sm:flex-initial"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      Page {currentPage} of {totalPages}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50 flex-1 sm:flex-initial"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edge Actions */}
      <EdgeActions
        onUpload={() => setBulkOpen(true)}
        onAdd={() => navigate(`/candidates/submit/${requirementId}`)}
        onFilter={() => setDrawerOpen(true)}
        showUpload={true}
        showAdd={true}
        showFilter={true}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Search & Filters"
        onClear={() => {
          setSearchTerm('');
          setStatusFilter('all');
          setScoreFilter('all');
          setAssignedFilter('all');
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search candidates by name, email, or skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-2 h-9 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full h-9 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
            >
              <option value="all">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="reviewing">Reviewing</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Score</label>
            <select
              value={scoreFilter}
              onChange={(e) => setScoreFilter(e.target.value)}
              className="w-full h-9 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
            >
              <option value="all">All Scores</option>
              <option value="excellent">Excellent (80%+)</option>
              <option value="good">Good (60-79%)</option>
              <option value="needs_review">Needs Review (&lt;60%)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Assignment</label>
            <select
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
              className="w-full h-9 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
            >
              <option value="all">All Assignment</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
              <option value="open_to_all">Open to All</option>
            </select>
          </div>
        </div>
      </FilterDrawer>

      <BulkUploadResumesModal
        requirementId={requirementId || ''}
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onInitiated={(_jobId, count) => {
          toast({ title: 'Bulk upload initiated', description: `${count} profile(s) initiated`, variant: 'success', duration: 4000 });
        }}
      />

      {/* Status Update Modal */}
      {selectedCandidateForStatusUpdate && requirementId && (
        <StatusUpdateModal
          isOpen={statusUpdateModalOpen}
          onClose={() => {
            setStatusUpdateModalOpen(false);
            setSelectedCandidateForStatusUpdate(null);
          }}
          candidateId={selectedCandidateForStatusUpdate.candidate_id || selectedCandidateForStatusUpdate.id}
          requirementId={requirementId}
          currentStatus={selectedCandidateForStatusUpdate.submission_status || 'Uploaded'}
          initialStatus={selectedCandidateForStatusUpdate.submission_status || 'Uploaded'}
          onStatusUpdated={() => {
            refetch();
            toast({ title: 'Success', description: 'Status updated successfully', variant: 'success', duration: 3000 });
          }}
        />
      )}

      {/* Assignment Modals - Only for HR Coordinators */}
      {canAssign && (
        <>
          <AssignmentModal
            isOpen={assignmentModalOpen}
            onClose={() => {
              setAssignmentModalOpen(false);
              setSelectedCandidate(null);
            }}
            onSubmit={handleAssignmentSubmit}
            recruiters={recruiters}
            candidateName={selectedCandidate?.name}
            isLoading={isAssigning}
          />
          
          <BulkAssignmentModal
            isOpen={bulkAssignmentModalOpen}
            onClose={() => {
              setBulkAssignmentModalOpen(false);
              setSelectedCandidatesForBulk([]);
            }}
            onSubmit={handleBulkAssignmentSubmit}
            candidates={selectedCandidatesForBulk}
            recruiters={recruiters}
            isLoading={isAssigning}
          />
        </>
      )}
    </ResponsiveContainer>
  );
};

export default RequirementCandidatesPage;